import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * A custom element that renders a variant-specific description beneath the media gallery.
 *
 * The description is server-rendered once for the initial variant. On the product page the
 * variant picker only morphs itself when the shopper switches variants, so this block would
 * otherwise stay frozen on the first variant. Listening for the variant update event and
 * swapping in the matching pre-rendered HTML keeps it in sync.
 *
 * @extends {Component}
 */
class ProductMediaDescription extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.updateDescription);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove from the reference captured on connect: by the time this runs the
    // element is already detached, so re-querying closest() would return null
    // and the listener would leak (it accumulates on each theme-editor re-render).
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.updateDescription);
    this.#section = null;
  }

  /**
   * Swaps the description to match the newly selected variant.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updateDescription = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // A missing element means "not in this render" (e.g. a combined-listing sibling
    // whose media column is absent), not "empty description". Leave the current
    // content untouched, matching the sibling variant-sync blocks (product-inventory).
    const newDescription = event.detail.data.html.querySelector(
      `product-media-description[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newDescription) return;

    // The block always renders this element, so a variant with no description yields
    // an empty one and assigning its HTML clears this block. Skip identical content
    // so variants that share a description don't churn the DOM, which would drop an
    // in-progress text selection, find highlights, or a screen reader's cursor.
    if (this.innerHTML.trim() === newDescription.innerHTML.trim()) return;
    this.innerHTML = newDescription.innerHTML;
  };
}

if (!customElements.get('product-media-description')) {
  customElements.define('product-media-description', ProductMediaDescription);
}
